<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { AvatarFallback, AvatarImage, AvatarRoot } from 'reka-ui';
import { ChevronDown, Menu, X, Check, Loader2 } from '@lucide/vue';
import { gotoLogin, clearToken } from '@/utils/auth';
import { useUserStore, useAppStore } from '@/stores';
import { useJoinCommunity } from '@/hooks/useJoinCommunity';
import logoFallback from '@/assets/logo.svg';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const appStore = useAppStore();
const { isJoined, joining, handleJoin } = useJoinCommunity();
const mobileMenuOpen = ref(false);

const { siteLogoUrl, siteName } = storeToRefs(appStore);
const logoSrc = computed(() => siteLogoUrl.value || logoFallback);
const logoAlt = computed(() => siteName.value || '');

const isLoggedIn = computed(() => Boolean(userStore.user.id));
const displayName = computed(() => userStore.user.nickname || userStore.user.username || '用户');
const userInitial = computed(() => displayName.value.charAt(0).toUpperCase());

const navLinks = [
  { label: '首页', path: '/' },
  { label: '案例库', path: '/cases' },
  { label: '贡献者库', path: '/experts' },
  { label: '社区资讯', path: '/news' },
];

const externalLinks = [
  { label: '开源法律课程', href: 'https://openatom.cn/law/lecture' },
];

function isActive(path: string) {
  if (path === '/') {
    return route.path === '/';
  }

  return route.path.startsWith(path);
}

function navigate(path: string) {
  mobileMenuOpen.value = false;
  router.push(path);
}

function toggleMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value;
}

const handleLogin = () => {
  gotoLogin();
};

function handleLogout() {
  clearToken();

  location.reload();
}
</script>

<template>
  <header
    class="sticky top-0 z-100 border-b border-border-subtle bg-[oklch(100%_0_0/0.85)] backdrop-blur-[16px]"
    :class="mobileMenuOpen ? 'max-lg:bg-surface-card max-lg:backdrop-blur-none' : ''"
  >
    <div class="container-main flex items-center justify-between h-16">
      <!-- 品牌 -->
      <div class="flex items-center gap-4">
        <RouterLink to="/" class="inline-flex items-center no-underline">
          <img
            :src="logoSrc"
            :alt="logoAlt"
            class="h-7 md:h-8 align-middle"
          >
        </RouterLink>
      </div>

      <!-- 桌面端导航 -->
      <nav aria-label="主导航" class="hidden lg:flex items-center gap-2">
        <template v-for="link in navLinks" :key="link.path">
          <button
            type="button"
            class="px-3 lg:px-4 py-2 rounded-[10px] text-sm font-medium transition-all duration-150 ease-out cursor-pointer border-none bg-transparent"
            :class="isActive(link.path)
              ? 'text-(--color-primary) bg-(--color-primary-light) font-semibold'
              : 'text-text-secondary hover:text-(--color-primary) hover:bg-(--color-primary-subtle)'"
            @click="navigate(link.path)"
          >
            {{ link.label }}
          </button>
        </template>

        <a
          v-for="ext in externalLinks"
          :key="ext.href"
          :href="ext.href"
          target="_blank"
          rel="noopener"
          class="px-3 lg:px-4 py-2 rounded-[10px] text-sm font-medium text-text-secondary transition-all duration-150 ease-out no-underline mr-4 hover:text-(--color-primary) hover:bg-(--color-primary-subtle)"
        >
          {{ ext.label }}
        </a>

        <button
          type="button"
          :disabled="joining || isJoined"
          class="inline-flex items-center gap-1.5 px-3 lg:px-4 py-2 rounded-[10px] text-xs font-semibold transition-all duration-250 ease-out whitespace-nowrap min-h-9 border-1.5 border-border-default text-text-primary bg-surface-card hover:border-(--color-primary) hover:text-(--color-primary) hover:bg-(--color-primary-subtle) disabled:opacity-60 disabled:cursor-default disabled:hover:border-border-default disabled:hover:text-text-primary disabled:hover:bg-surface-card"
          @click="handleJoin"
        >
          <Loader2 v-if="joining" :size="14" class="animate-spin" />
          <Check v-else-if="isJoined" :size="14" />
          {{ isJoined ? '已加入社区' : '加入社区' }}
        </button>

        <!-- 未登录 -->
        <button
          v-if="!isLoggedIn"
          type="button"
          class="px-3 lg:px-4 py-2 rounded-[10px] text-xs font-semibold transition-all duration-250 ease-out no-underline whitespace-nowrap min-h-9 bg-(--color-primary) text-white shadow-[var(--shadow-sm)] hover:bg-(--color-primary-hover) hover:shadow-[var(--shadow-md)] hover:-translate-y-px cursor-pointer border-none"
          @click="handleLogin"
        >
          登录
        </button>

        <!-- 已登录 -->
        <DropdownMenu v-else :modal="false">
          <DropdownMenuTrigger as-child>
            <button
              type="button"
              class="flex items-center gap-1.5 cursor-pointer border-none bg-transparent p-0 outline-none"
            >
              <AvatarRoot class="w-8 h-8 rounded-full border-2 border-border-default hover:border-(--color-primary) transition-colors duration-150">
                <AvatarImage
                  v-if="userStore.user.photo"
                  :src="userStore.user.photo"
                  alt="用户头像"
                  class="w-full h-full rounded-full object-cover"
                />
                <AvatarFallback
                  :delay-ms="600"
                  class="w-full h-full flex items-center justify-center bg-(--color-primary) text-white text-sm font-semibold rounded-full"
                >
                  {{ userInitial }}
                </AvatarFallback>
              </AvatarRoot>
              <ChevronDown class="size-3 text-text-tertiary transition-transform duration-200" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent class="z-200">
            <!-- 用户信息 -->
            <div class="px-3 py-2.5">
              <p class="text-sm font-semibold text-text-primary leading-tight">{{ displayName }}</p>
              <p class="text-xs text-text-tertiary leading-tight mt-0.5">@{{ userStore.user.username }}</p>
            </div>

            <DropdownMenuSeparator />

            <DropdownMenuItem class="text-(--color-destructive) cursor-pointer" @click="handleLogout">
              退出登录
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </nav>

      <!-- 移动端汉堡按钮 -->
      <button
        type="button"
        class="lg:hidden flex items-center justify-center w-10 h-10 rounded-[10px] border border-border-default bg-transparent cursor-pointer"
        aria-label="Toggle menu"
        @click="toggleMenu"
      >
        <X v-if="mobileMenuOpen" :size="20" class="text-text-secondary" />
        <Menu v-else :size="20" class="text-text-secondary" />
      </button>
    </div>

    <!-- 移动端菜单面板 -->
    <div
      class="lg:hidden absolute top-full left-0 right-0 bg-surface-card shadow-[var(--shadow-lg)] overflow-hidden transition-all duration-300 ease-out"
      :class="mobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'"
    >
      <div class="container-main py-4 flex flex-col">
        <!-- 导航链接 -->
        <nav aria-label="移动端导航" class="flex flex-col gap-1">
          <button
            v-for="link in navLinks"
            :key="link.path"
            type="button"
            class="w-full text-left px-4 py-3 rounded-[10px] text-sm font-medium transition-all duration-150 cursor-pointer border-none"
            :class="isActive(link.path)
              ? 'bg-(--color-primary-light) text-(--color-primary) font-semibold'
              : 'bg-transparent text-text-secondary hover:bg-(--color-primary-subtle)'"
            @click="navigate(link.path)"
          >
            {{ link.label }}
          </button>

          <a
            v-for="ext in externalLinks"
            :key="ext.href"
            :href="ext.href"
            target="_blank"
            rel="noopener"
            class="px-4 py-3 rounded-[10px] text-sm font-medium text-text-secondary no-underline hover:bg-(--color-primary-subtle)"
          >
            {{ ext.label }}
          </a>
        </nav>

        <!-- 分隔线 -->
        <div class="border-t border-border-subtle my-3"></div>

        <!-- 操作按钮 -->
        <div class="flex flex-col gap-2">
          <button
            type="button"
            :disabled="joining || isJoined"
            class="w-full inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-[10px] text-sm font-semibold transition-all duration-250 border-1.5 border-border-default text-text-primary hover:border-(--color-primary) hover:text-(--color-primary) hover:bg-(--color-primary-subtle) disabled:opacity-60 disabled:cursor-default disabled:hover:border-border-default disabled:hover:text-text-primary disabled:hover:bg-surface-card"
            @click="handleJoin"
          >
            <Loader2 v-if="joining" :size="16" class="animate-spin" />
            <Check v-else-if="isJoined" :size="16" />
            {{ isJoined ? '已加入社区' : '加入社区' }}
          </button>

          <template v-if="!isLoggedIn">
            <button
              type="button"
              class="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-[10px] text-sm font-semibold transition-all duration-250 cursor-pointer border-none bg-(--color-primary) text-white hover:bg-(--color-primary-hover)"
              @click="handleLogin"
            >
              登录
            </button>
          </template>

          <template v-else>
            <!-- 已登录用户信息 -->
            <div class="flex items-center gap-3 px-4 py-3">
              <AvatarRoot class="w-10 h-10 rounded-full border-2 border-border-default shrink-0">
                <AvatarImage
                  v-if="userStore.user.photo"
                  :src="userStore.user.photo"
                  alt="用户头像"
                  class="w-full h-full rounded-full object-cover"
                />
                <AvatarFallback
                  :delay-ms="600"
                  class="w-full h-full flex items-center justify-center bg-(--color-primary) text-white text-sm font-semibold rounded-full"
                >
                  {{ userInitial }}
                </AvatarFallback>
              </AvatarRoot>
              <div class="flex flex-col min-w-0">
                <span class="text-sm font-semibold text-text-primary leading-tight">{{ displayName }}</span>
                <span class="text-xs text-text-tertiary leading-tight mt-0.5">@{{ userStore.user.username }}</span>
              </div>
            </div>

            <button
              type="button"
              class="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-[10px] text-sm font-semibold transition-all duration-250 cursor-pointer border-none bg-red-50 text-(--color-destructive) hover:bg-red-100"
              @click="handleLogout"
            >
              退出登录
            </button>
          </template>
        </div>
      </div>
    </div>
  </header>
</template>
